/**
 * Maps Portable Ice System Role Engine + Compatibility Rules output
 * into customer-facing CompatibilityResult props.
 *
 * Does not resolve pair compatibility. Does not rank products.
 * Canonical catalog identity only.
 */

const { evaluateSystemBuild } = require('../../domain/systemRoleEngine');
const { compatibilityRules } = require('../../domain/compatibilityRules');
const canonicalUniverse = require('../../domain/canonicalUniverse.json');

const RESULT_STATES = Object.freeze({
  WORKS_TOGETHER: 'WORKS_TOGETHER',
  DOES_NOT_WORK: 'DOES_NOT_WORK',
  NEEDS_REVIEW: 'NEEDS_REVIEW',
});

const RESULT_LABELS = Object.freeze({
  WORKS_TOGETHER: 'Works Together',
  DOES_NOT_WORK: 'Does Not Work',
  NEEDS_REVIEW: 'Needs Review',
});

const ACTION_GROUPS = Object.freeze({
  KEEP: 'KEEP',
  ADD: 'ADD',
  REPLACE: 'REPLACE',
  NOT_USED: 'NOT USED',
});

const ELECTRONICS_ROLES = Object.freeze([
  'DISPLAY',
  'LIVE_SONAR',
  'SONAR_PROCESSOR_INTERFACE',
]);

const MISSING_ROLE_COPY = Object.freeze({
  PORTABLE_PLATFORM: {
    title: 'Choose a compatible portable platform',
    explanation:
      "A portable platform is still needed. Angler Built is not choosing one product when more than one verified option can fill this role.",
  },
  POWER_SOURCE: {
    title: 'Choose a compatible power source',
    explanation:
      'A power source is still needed. Angler Built is not choosing a battery when more than one option can fill this role.',
  },
  TRANSDUCER_POSITIONING: {
    title: 'Choose compatible transducer positioning',
    explanation:
      'Transducer positioning is still needed. Physical pole fit is only confirmed when verified compatibility data exists.',
  },
  DISPLAY: {
    title: 'Choose a compatible display',
    explanation: 'A display is still needed for this setup.',
  },
  LIVE_SONAR: {
    title: 'Choose a compatible LiveScope system',
    explanation: 'A LiveScope system is still needed for this setup.',
  },
  SONAR_PROCESSOR_INTERFACE: {
    title: 'A sonar processor is still required',
    explanation:
      'This architecture still needs a processor, and Angler Built does not have a single verified product to name yet.',
  },
});

const ROLE_LABELS = Object.freeze({
  DISPLAY: 'Display',
  LIVE_SONAR: 'Live sonar',
  SONAR_PROCESSOR_INTERFACE: 'Sonar processor',
  POWER_SOURCE: 'Power',
  PORTABLE_PLATFORM: 'Portable platform',
  TRANSDUCER_POSITIONING: 'Transducer positioning',
});

const FAMILY_SYSTEM_LABEL = Object.freeze({
  'garmin-livescope-2': 'LiveScope 2',
  'garmin-livescope-2-hd': 'LiveScope 2 HD',
  'garmin-lvs34': 'LiveScope Plus',
  'garmin-lvs62': 'LiveScope XR',
  'garmin-lvs32': 'LiveScope',
});

function unique(values) {
  return [...new Set((values || []).filter(Boolean))];
}

function productBySlug(catalog, slug) {
  if (!slug) return null;
  return (catalog?.products || []).find((row) => row.slug === slug) || null;
}

function familyBySlug(catalog, slug) {
  if (!slug) return null;
  return (catalog?.hardwareFamilies || []).find((row) => row.slug === slug) || null;
}

function toProductMeta(catalog, slug) {
  const product = productBySlug(catalog, slug);
  const family = familyBySlug(catalog, product?.hardwareFamilySlug);
  return {
    slug,
    name: product?.name || slug,
    brand: product?.brand || family?.brand || null,
    model: product?.modelNumber || null,
    family: family?.name || product?.hardwareFamilySlug || null,
    familySlug: product?.hardwareFamilySlug || null,
    imageUrl: product?.imageUrl || product?.image_url || null,
    roles: product?.roles || [],
    entityKind: product?.entityKind || null,
  };
}

function pairsWithBothSides(build) {
  return (build?.compatibilityResults || []).filter((pair) => pair?.subject && pair?.object);
}

function primaryPair(build) {
  const pairs = pairsWithBothSides(build);
  if (!pairs.length) return null;
  const rank = {
    INCOMPATIBLE: 3,
    NOT_FULLY_VERIFIED: 2,
    COMPATIBLE_WITH_REQUIREMENT: 1,
    COMPATIBLE: 0,
  };
  return [...pairs].sort((a, b) => (rank[b.status] || 0) - (rank[a.status] || 0))[0];
}

function mapPairStatusToResult(status) {
  if (status === 'INCOMPATIBLE') return RESULT_STATES.DOES_NOT_WORK;
  if (status === 'NOT_FULLY_VERIFIED') return RESULT_STATES.NEEDS_REVIEW;
  if (status === 'COMPATIBLE' || status === 'COMPATIBLE_WITH_REQUIREMENT') {
    return RESULT_STATES.WORKS_TOGETHER;
  }
  return RESULT_STATES.NEEDS_REVIEW;
}

function systemLabel(productMeta) {
  if (!productMeta) return 'the selected LiveScope system';
  return FAMILY_SYSTEM_LABEL[productMeta.familySlug] || productMeta.name;
}

function sonarShortName(productMeta) {
  return productMeta?.model || productMeta?.name || 'selected LiveScope';
}

function explanationFromPair(pair, catalog) {
  if (!pair) {
    return {
      copyKey: 'NO_PAIR',
      text: "We don't have enough verified compatibility data to confirm this connection yet.",
    };
  }

  const display = toProductMeta(catalog, pair.object?.slug);
  const sonar = toProductMeta(catalog, pair.subject?.slug);
  const missing = (pair.requirements || []).filter((row) => !row.satisfied);
  const satisfiedRequirements = (pair.requirements || []).filter((row) => row.satisfied);
  const missingLabels = missing.map((row) => toProductMeta(catalog, row.productSlug).name);
  const hasSatisfiedRequirement = satisfiedRequirements.length > 0;

  if (pair.status === 'INCOMPATIBLE') {
    return {
      copyKey: 'PAIR_INCOMPATIBLE',
      text: 'This display is not verified compatible with the selected LiveScope system.',
    };
  }

  if (pair.status === 'NOT_FULLY_VERIFIED') {
    return {
      copyKey: 'PAIR_NOT_FULLY_VERIFIED',
      text: "We don't have enough verified compatibility data to confirm this connection yet.",
    };
  }

  if (pair.status === 'COMPATIBLE_WITH_REQUIREMENT' && missing.length) {
    return {
      copyKey: 'PAIR_COMPATIBLE_MISSING_REQUIREMENT',
      text: `Your ${display.name} can be used with ${sonar.name}. This setup requires ${missingLabels.join(', ')}.`,
    };
  }

  const satisfiedLabels = unique(
    satisfiedRequirements.map((row) => toProductMeta(catalog, row.productSlug).name)
  );

  if (pair.status === 'COMPATIBLE' && hasSatisfiedRequirement) {
    return {
      copyKey: 'PAIR_COMPATIBLE_REQUIREMENTS_MET',
      text: `Your ${display.name} can be used with ${sonar.name}. ${satisfiedLabels.join(', ')} ${satisfiedLabels.length === 1 ? 'is' : 'are'} in place for this setup.`,
    };
  }

  if (pair.status === 'COMPATIBLE' && (pair.requirements || []).length === 0) {
    return {
      copyKey: 'PAIR_COMPATIBLE_DIRECT',
      text: `Your ${display.name} can be used with ${systemLabel(sonar)}. The ${sonarShortName(sonar)} connects directly and does not require a GLS 10.`,
    };
  }

  return {
    copyKey: 'PAIR_COMPATIBLE',
    text: `Your ${display.name} can be used with ${sonar.name}.`,
  };
}

function preferredSource(sources) {
  const list = sources || [];
  if (list.some((row) => row.source === 'OWNED')) return 'OWNED';
  if (list.some((row) => row.source === 'SELECTED')) return 'SELECTED';
  if (list.some((row) => row.source === 'PACKAGE_CONTENT')) return 'PACKAGE_CONTENT';
  return list[0]?.source || null;
}

function componentRecord(build, slug) {
  return (build?.expandedComponents || []).find((row) => row.slug === slug) || null;
}

function actionItem(catalog, slug, extras = {}) {
  const meta = toProductMeta(catalog, slug);
  return {
    slug,
    name: meta.name,
    brand: meta.brand,
    model: meta.model,
    family: meta.family,
    imageUrl: meta.imageUrl,
    roleLabel: extras.roleLabel || null,
    role: extras.role || null,
    source: extras.source || null,
    owned: extras.source === 'OWNED',
    reasonCode: extras.reasonCode || null,
    explanation: extras.explanation || null,
  };
}

function buildActions(build, catalog, pair) {
  const keep = [];
  const add = [];
  const replace = [];
  const notUsed = [];
  const keepSlugs = new Set();
  const replaceSlugs = new Set();
  const addSlugs = new Set();
  const notUsedSlugs = new Set();

  const incompatibleDisplays = unique(
    pairsWithBothSides(build)
      .filter((row) => row.status === 'INCOMPATIBLE')
      .map((row) => row.object?.slug)
  );
  incompatibleDisplays.forEach((slug) => replaceSlugs.add(slug));

  for (const slug of incompatibleDisplays) {
    const component = componentRecord(build, slug);
    const source = preferredSource(component?.sources);
    replace.push(
      actionItem(catalog, slug, {
        role: 'DISPLAY',
        roleLabel: ROLE_LABELS.DISPLAY,
        source,
        reasonCode: 'REPLACE_VERIFIED_INCOMPATIBLE',
        explanation: 'This display is verified incompatible with the selected LiveScope system and should be replaced.',
      })
    );
  }

  for (const assessment of build?.componentAssessments || []) {
    if (assessment.status !== 'REDUNDANT_OR_NOT_REQUIRED') continue;
    if (notUsedSlugs.has(assessment.slug)) continue;
    notUsedSlugs.add(assessment.slug);
    const component = componentRecord(build, assessment.slug);
    notUsed.push(
      actionItem(catalog, assessment.slug, {
        role: 'SONAR_PROCESSOR_INTERFACE',
        roleLabel: ROLE_LABELS.SONAR_PROCESSOR_INTERFACE,
        source: preferredSource(component?.sources),
        reasonCode: 'REDUNDANT_OR_NOT_REQUIRED',
        explanation: 'This component is not required in the selected architecture. It does not make the setup incompatible.',
      })
    );
  }

  for (const req of build?.missingCompatibilityRequirements || []) {
    if (!req.productSlug) continue;
    if (addSlugs.has(req.productSlug)) continue;
    addSlugs.add(req.productSlug);
    const requiredMeta = toProductMeta(catalog, req.productSlug);
    const role = (requiredMeta.roles || []).find((name) => ROLE_LABELS[name]) || null;
    add.push(
      actionItem(catalog, req.productSlug, {
        role,
        roleLabel: role ? ROLE_LABELS[role] : null,
        source: null,
        reasonCode: 'UNIQUE_VERIFIED_REQUIREMENT',
        explanation: `This setup requires ${requiredMeta.name}.`,
      })
    );
  }

  for (const roleName of ELECTRONICS_ROLES) {
    const roleRow = (build?.roles || []).find((row) => row.role === roleName);
    if (!roleRow || roleRow.status !== 'SATISFIED') continue;
    for (const holder of roleRow.satisfiedBy || []) {
      if (!holder?.slug) continue;
      if (replaceSlugs.has(holder.slug) || notUsedSlugs.has(holder.slug) || addSlugs.has(holder.slug)) {
        continue;
      }
      if (keepSlugs.has(holder.slug)) continue;
      keepSlugs.add(holder.slug);
      const source = holder.source || preferredSource(componentRecord(build, holder.slug)?.sources);
      const ownedPhrase =
        source === 'OWNED'
          ? 'You already have this component, and it remains valid for this setup.'
          : 'This selected component remains valid for this setup.';
      keep.push(
        actionItem(catalog, holder.slug, {
          role: roleName,
          roleLabel: ROLE_LABELS[roleName],
          source,
          reasonCode: source === 'OWNED' ? 'KEEP_COMPATIBLE_OWNED_GEAR' : 'KEEP_COMPATIBLE_SELECTED_GEAR',
          explanation: ownedPhrase,
        })
      );
    }
  }

  return { keep, add, replace, notUsed };
}

function catalogCandidatesForRole(catalog, role) {
  return (catalog?.products || []).filter(
    (row) =>
      row?.entityKind !== 'package'
      && Array.isArray(row.roles)
      && row.roles.includes(role)
  );
}

function buildUnresolved(build, catalog, pair, actions) {
  const unresolved = [];
  const addedSlugs = new Set((actions.add || []).map((row) => row.slug));

  for (const item of build?.unresolvedItems || []) {
    if (item.kind === 'COMPATIBILITY') {
      unresolved.push({
        kind: 'COMPATIBILITY',
        title: 'Needs Review',
        explanation: "We don't have enough verified compatibility data to confirm this connection yet.",
        sonarSlug: item.sonarSlug || null,
        displaySlug: item.displaySlug || null,
        candidateSlugs: [],
      });
    }
    if (item.kind === 'POLE_TRANSDUCER') {
      unresolved.push({
        kind: 'POLE_TRANSDUCER',
        title: 'Needs Review',
        explanation:
          "We don't have enough verified compatibility data to confirm this pole and transducer connection yet.",
        sonarSlug: item.sonarSlug || null,
        poleSlug: item.poleSlug || null,
        candidateSlugs: [],
      });
    }
    if (item.kind === 'PACKAGE_CYCLE') {
      unresolved.push({
        kind: 'PACKAGE_CYCLE',
        title: 'Needs Review',
        explanation: 'Package membership could not be fully resolved, so Angler Built is not confirming this setup.',
        candidateSlugs: [],
      });
    }
  }

  const uniqueRequirementRoles = new Set();
  for (const slug of addedSlugs) {
    for (const role of toProductMeta(catalog, slug).roles || []) {
      uniqueRequirementRoles.add(role);
    }
  }

  for (const missing of build?.missingRequiredRoles || []) {
    if (uniqueRequirementRoles.has(missing.role)) continue;
    const copy = MISSING_ROLE_COPY[missing.role];
    if (!copy) continue;
    const candidates = catalogCandidatesForRole(catalog, missing.role);
    unresolved.push({
      kind: 'MISSING_ROLE_CANDIDATES',
      role: missing.role,
      title: copy.title,
      explanation: copy.explanation,
      candidateSlugs: candidates.length > 1 ? candidates.map((row) => row.slug) : [],
    });
  }

  for (const req of build?.missingCompatibilityRequirements || []) {
    if (req.productSlug) continue;
    unresolved.push({
      kind: 'UNRESOLVED_REQUIREMENT',
      title: 'Needs Review',
      explanation:
        "A required component is missing, but Angler Built does not have a single verified product to name yet.",
      hardwareFamilySlug: req.hardwareFamilySlug || null,
      candidateSlugs: [],
    });
  }

  return unresolved;
}

function selectedProducts(build, catalog, pair) {
  const slugs = unique([
    pair?.object?.slug,
    pair?.subject?.slug,
    ...((build?.roles || [])
      .filter((row) => row.role === 'DISPLAY' || row.role === 'LIVE_SONAR')
      .flatMap((row) => (row.satisfiedBy || []).map((holder) => holder.slug))),
  ]);
  return slugs
    .map((slug) => {
      const meta = toProductMeta(catalog, slug);
      const component = componentRecord(build, slug);
      const role = (meta.roles || []).includes('DISPLAY')
        ? 'DISPLAY'
        : (meta.roles || []).includes('LIVE_SONAR')
          ? 'LIVE_SONAR'
          : null;
      if (!role) return null;
      return {
        ...meta,
        role,
        roleLabel: ROLE_LABELS[role],
        source: preferredSource(component?.sources),
      };
    })
    .filter(Boolean);
}

function verificationState(pair) {
  if (!pair) return 'UNVERIFIED';
  if (pair.verificationStatus === 'VERIFIED') return 'VERIFIED';
  if (pair.status === 'NOT_FULLY_VERIFIED') return 'NOT_FULLY_VERIFIED';
  return pair.verificationStatus || 'UNVERIFIED';
}

function buildCompatibilityResultViewModel(build, catalog = canonicalUniverse) {
  const pair = primaryPair(build);
  const resultKey = pair
    ? mapPairStatusToResult(pair.status)
    : RESULT_STATES.NEEDS_REVIEW;
  const explanation = explanationFromPair(pair, catalog);
  const actions = buildActions(build, catalog, pair);
  const unresolved = buildUnresolved(build, catalog, pair, actions);
  const products = selectedProducts(build, catalog, pair);
  const productSlugs = unique([
    ...(build?.selection?.selectedProductIds || []),
    ...(build?.selection?.ownedProductIds || []),
    ...products.map((row) => row.slug),
  ]);

  return {
    resultKey,
    resultLabel: RESULT_LABELS[resultKey],
    explanation: explanation.text,
    explanationKey: explanation.copyKey,
    pairStatus: pair?.status || null,
    selectedProducts: products,
    actions,
    unresolved,
    analytics: {
      event: 'compatibility_checked',
      products: productSlugs,
      result: resultKey,
      requiredComponentCount: actions.add.length,
      verificationState: verificationState(pair),
    },
  };
}

const ICE_MISSING_ROLES = new Set(['POWER_SOURCE', 'PORTABLE_PLATFORM', 'TRANSDUCER_POSITIONING']);

function presentCompatibilityResultViewModel(viewModel, options = {}) {
  if (!viewModel) return viewModel;
  if (options.includeIceCompleteness !== false) return viewModel;
  return {
    ...viewModel,
    unresolved: (viewModel.unresolved || []).filter(
      (item) => !(item.kind === 'MISSING_ROLE_CANDIDATES' && ICE_MISSING_ROLES.has(item.role))
    ),
  };
}

function evaluateCompatibilityResult(input = {}) {
  const catalog = input.catalog || canonicalUniverse;
  const rules = input.rules || compatibilityRules;
  const build = evaluateSystemBuild({
    templateSlug: input.templateSlug || 'portable-livescope-ice',
    selectedProductIds: input.selectedProductIds || input.selectedSlugs || [],
    ownedProductIds: input.ownedProductIds || input.ownedSlugs || [],
    catalog,
    rules,
  });
  return {
    build,
    viewModel: presentCompatibilityResultViewModel(
      buildCompatibilityResultViewModel(build, catalog),
      { includeIceCompleteness: input.includeIceCompleteness }
    ),
  };
}

module.exports = {
  RESULT_STATES,
  RESULT_LABELS,
  ACTION_GROUPS,
  buildCompatibilityResultViewModel,
  presentCompatibilityResultViewModel,
  evaluateCompatibilityResult,
  toProductMeta,
};
